/**
 * To-dos der Übersicht aus echten Zählern (`requestCounts`, `confirmedVsDesired`): je Apartment
 * höchstens eines, das dringendste – fehlgeschlagen vor aufgegeben vor Abweichung vor unbestätigt.
 */
import type { DynamicPricingApartmentOverview } from '@/shared/api';
import { days, type Tone } from './overviewModel';

export interface Todo {
  propertyId: string;
  kind: 'Fehler' | 'Aufgegeben' | 'Abweichung' | 'Unbestätigt';
  tone: Exclude<Tone, 'green' | 'neutral'>;
  title: string;
  sub: string;
  target: 'history' | 'month';
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 'n'}`;
const on = (count: number) => (count === 1 ? 'an 1 Tag' : `an ${count} Tagen`);

export function todos(apartments: readonly DynamicPricingApartmentOverview[]): Todo[] {
  return apartments.flatMap((apartment): Todo[] => {
    const { requestCounts: requests, confirmedVsDesired: drift } = apartment;
    const base = { propertyId: apartment.propertyId };
    if (requests.failed > 0) {
      const givenUp = drift.givenUpDays > 0 ? `, ${days(drift.givenUpDays)} davon aufgegeben` : '';
      return [
        {
          ...base,
          kind: 'Fehler',
          tone: 'red',
          target: 'history',
          title: `${apartment.name} · Berechnung fehlgeschlagen`,
          // Der Server zählt nur Fehler seit dem letzten erfolgreichen Lauf.
          sub: `${plural(requests.failed, 'fehlgeschlagene Anfrage')} seit dem letzten Lauf · ${days(drift.daysWithDrift)} weichen vom Ziel ab${givenUp}.`,
        },
      ];
    }
    if (drift.givenUpDays > 0) {
      return [
        {
          ...base,
          kind: 'Aufgegeben',
          tone: 'red',
          target: 'history',
          title: `${apartment.name} · ${days(drift.givenUpDays)} aufgegeben`,
          sub: 'Channex hat den Wert wiederholt anders bestätigt; die Automatik sendet ihn nicht erneut.',
        },
      ];
    }
    // Tage ohne bestätigten Stand zählen in den Abweichungen mit – sie werden dort genannt.
    if (drift.daysWithDrift > drift.missingConfirmedDays) {
      const parts = [
        drift.rateDriftDays > 0 && `Preis ${on(drift.rateDriftDays)}`,
        drift.minimumStayDriftDays > 0 && `Mindestnächte ${on(drift.minimumStayDriftDays)}`,
        drift.missingConfirmedDays > 0 &&
          `davon ${days(drift.missingConfirmedDays)} ohne bestätigten Stand`,
        requests.pending > 0 && `${plural(requests.pending, 'Anfrage')} offen`,
      ].filter(Boolean);
      return [
        {
          ...base,
          kind: 'Abweichung',
          tone: 'amber',
          target: 'month',
          title: `${apartment.name} · ${days(drift.daysWithDrift)}: Ziel noch nicht bestätigt`,
          sub:
            parts.length > 0
              ? `${parts.join(' · ')}.`
              : 'Die Rückmeldung von Channex steht noch aus.',
        },
      ];
    }
    if (drift.missingConfirmedDays > 0) {
      return [
        {
          ...base,
          kind: 'Unbestätigt',
          tone: 'amber',
          target: 'history',
          title: `${apartment.name} · ${days(drift.missingConfirmedDays)} ohne bestätigten Stand`,
          sub: 'Die Rückmeldung von Channex steht noch aus.',
        },
      ];
    }
    return [];
  });
}
